import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { GoogleMap, InfoWindow } from "@react-google-maps/api";
import MapaMultipleStaticMarkers from "./MapaMultipleStaticMarkers";
import DynamicMarkers from "./DynamicMarkers";

import farmaciaIcon from "../../../../assets/map/icons/farmacia.svg";
import casaIcon from "../../../../assets/map/icons/casita.png";
import MapaInfoDinamico from "./MapaInfoDinamico";

import {
  calcularZoom,
  getMaxDistanceBetweenPoints,
} from "../../../Pantalla/helper/funcionesMapa";

export function MapaMultiple(props) {
  const { cab, id } = props;

  const mapaInfoConfig = cab.sc_hijos?.filter(
    (f) => f.componente === "mapa_info"
  );
  const hayMapaInfo = mapaInfoConfig.length > 0;
  const data = useMemo(() => {
    const data = [];
    mapaInfoConfig.forEach((mapaInfo) => {
      mapaInfo.datos.forEach((d) => {
        const latitud =
          d[mapaInfo.mapa_latitud] && Number(d[mapaInfo.mapa_latitud]);
        const longitud =
          d[mapaInfo.mapa_longitud] && Number(d[mapaInfo.mapa_longitud]);

        if (latitud && !isNaN(latitud) && longitud && !isNaN(longitud)) {
          data.push({ latitud, longitud });
        }
      });
    });
    return data;
  }, [mapaInfoConfig]);

  const maxDistance = useMemo(() => getMaxDistanceBetweenPoints(data), [data]);
  const [zoom, setZoom] = useState(16 - Math.log2(maxDistance.distanceKm));

  const [, forceUpdate] = useState(false);
  // const [map, setMap] = useState(null);
  const showInfoWindowRef = useRef(false);
  const selectedElementRef = useRef(null);
  const activeMarkerPosRef = useRef(null);
  const activeMarkerCab = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const centerRef = useRef(null);
  const mapRef = useRef(null);

  const center = useMemo(() => {
    if (!data || data.length === 0) {
      return { lat: -32.952559, lng: -60.67335 };
    }

    let latSum = 0;
    let lngSum = 0;
    let count = 0;

    data.forEach((d) => {
      const lat = Number(d.latitud);
      const lng = Number(d.longitud);

      if (!isNaN(lat) && !isNaN(lng)) {
        latSum += lat;
        lngSum += lng;
        count++;
      }
    });

    // Si no quedó ningún valor válido, devolvemos el fallback
    if (count === 0) {
      return { lat: -32.952559, lng: -60.67335 };
    }

    const latm = latSum / count;
    const lngm = lngSum / count;

    // Si por alguna razón el cálculo falla, también devolvemos el fallback
    if (isNaN(latm) || isNaN(lngm)) {
      return { lat: -32.952559, lng: -60.67335 };
    }

    return { lat: latm, lng: lngm };
  }, [data]);
  if (centerRef.current === null) centerRef.current = center;

  const handleMouseOut = useCallback(() => {
    if (!hayMapaInfo) return;
    hoverTimeoutRef.current = setTimeout(() => {
      selectedElementRef.current = null;
      activeMarkerPosRef.current = null;
      showInfoWindowRef.current = false;
      activeMarkerCab.current = null;
      forceUpdate((f) => !f);
    }, 300);
  }, [hayMapaInfo]);

  const handleMouseOver = useCallback(
    (element, e, cab) => {
      const prev = selectedElementRef.current;
      if (!hayMapaInfo) return;
      if (prev?._key !== element._key) {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        selectedElementRef.current = element;
        if (e?.latLng) {
          activeMarkerPosRef.current = {
            lat: e.latLng.lat(),
            lng: e.latLng.lng(),
          };
        }
        activeMarkerCab.current = cab;
        showInfoWindowRef.current = true;
        forceUpdate((f) => !f);
        // center on marker without re-rendering map
        if (mapRef.current && activeMarkerPosRef.current) {
          mapRef.current.panTo(activeMarkerPosRef.current);
        }
      }
    },
    [hayMapaInfo]
  );

  // Opciones de contenedor del mapa
  const containerStyle = useMemo(() => {
    return { width: cab.width || "95%", height: cab.height || "200px" };
  }, [cab.height, cab.width]);

  const googleMapStyle = useMemo(() => {
    return { width: "100%", height: cab.height || "200px" };
  }, [cab.height]);

  const icon = useMemo(
    () => ({
      farmacia: { icon: farmaciaIcon, point: [0, 25] },
      cliente: { icon: casaIcon, point: [25, 25] },
    }),
    []
  );

  const memoizedMarkers = useMemo(
    () =>
      mapaInfoConfig.map((cab) => (
        <MapaMultipleStaticMarkers
          cab={cab}
          icon={icon}
          handleMouseOut={handleMouseOut}
          handleMouseOver={handleMouseOver}
          markers={cab.datos}
          key={cab.id_a}
        />
      )),
    [handleMouseOut, handleMouseOver, icon, mapaInfoConfig]
  );

  useLayoutEffect(() => {
    const rect = document.getElementById(cab.id_a).getBoundingClientRect();
    const z =
      cab.mapa_zoom && cab.mapa_zoom !== "" && !isNaN(parseFloat(cab.mapa_zoom))
        ? parseFloat(cab.mapa_zoom)
        : calcularZoom(rect.width, rect.height, maxDistance);
    setZoom(z);
  }, [cab.id_a, cab.mapa_zoom, maxDistance]);

  return (
    <div id={cab.id_a} className="map_container" style={containerStyle}>
      <GoogleMap
        mapContainerStyle={googleMapStyle}
        tilt={12}
        center={centerRef.current}
        zoom={zoom}
        onLoad={(map) => {
          map.setOptions({ streetViewControl: false });
          mapRef.current = map;
          // setMap(map);
        }}
        onIdle={() => {
          centerRef.current = mapRef.current.getCenter().toJSON();
        }}
      >
        {hayMapaInfo && memoizedMarkers}
        {cab.mapa_markers_dinamicos ? (
          <DynamicMarkers icon={icon} cab={cab} id={id} />
        ) : (
          <></>
        )}
        {showInfoWindowRef.current && hayMapaInfo && (
          <InfoWindow
            position={activeMarkerPosRef.current}
            onCloseClick={handleMouseOut}
          >
            {selectedElementRef.current && (
              <MapaInfoDinamico
                selectedElement={selectedElementRef.current}
                cab={activeMarkerCab.current}
                mapaInfoConfig={mapaInfoConfig}
              />
            )}
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
}

export default MapaMultiple;
